import { Skeleton, Stack, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { LightningBoltIcon } from './LightningBoltIcon';
import { LightningTotalIcon } from './LightningTotalIcon';
import { StrikeCounts } from './LightningLayer.types';

type LightningTimelineLegendProps = {
  counts: StrikeCounts | null;
  isLoading: boolean;
};

type LegendItemProps = {
  icon: JSX.Element;
  label: string;
  value: number | null;
  color: string;
  isLoading: boolean;
};

const LegendItem = ({
  icon,
  label,
  value,
  color,
  isLoading,
}: LegendItemProps): JSX.Element => (
  <Stack
    direction="row"
    spacing={0.5}
    sx={{
      flex: '1 1 0',
      minWidth: 0,
      alignItems: 'center',
      justifyContent: 'center',
      color,
    }}
  >
    {icon}
    <Typography variant="body2" color="inherit" noWrap>
      {label}
    </Typography>
    {isLoading ? (
      <Skeleton variant="text" width={24} />
    ) : (
      <Typography variant="body2" color="inherit" fontWeight={700}>
        {value === null ? '-' : value.toLocaleString()}
      </Typography>
    )}
  </Stack>
);

export const LightningTimelineLegend = ({
  counts,
  isLoading,
}: LightningTimelineLegendProps): JSX.Element => {
  const { t } = useTranslation();
  const theme = useTheme();

  return (
    <Stack
      direction="row"
      sx={{
        height: '100%',
        px: 1,
        alignItems: 'center',
        backgroundColor: 'background.secondary',
      }}
    >
      <LegendItem
        icon={<LightningBoltIcon backgroundColor="brand.500" />}
        label={t('map.timelineLayers.lightning.legend.positive')}
        value={counts?.positive ?? null}
        color="brand.500"
        isLoading={isLoading}
      />
      <LegendItem
        icon={<LightningBoltIcon backgroundColor="warmGrey.500" />}
        label={t('map.timelineLayers.lightning.legend.negative')}
        value={counts?.negative ?? null}
        color="warmGrey.500"
        isLoading={isLoading}
      />
      <LegendItem
        icon={
          <LightningTotalIcon
            sx={{ fontSize: 16 }}
            outlineColor={theme.palette.background.secondary}
          />
        }
        label={t('map.timelineLayers.lightning.legend.total')}
        value={counts?.total ?? null}
        color="text.primary"
        isLoading={isLoading}
      />
    </Stack>
  );
};
